import style from './textInput.module.scss';
import type { BaseFieldProps, UiStateProps } from '.';
import { joinNames } from '../helpers';
import NotificationIcon from '../NotificationIcon';
import { ErrorMessage } from '../Container';
import Label from '../Label';
import { useHelpBox } from '../HelpBox';

export type TextInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & {
    type: Exclude<React.HTMLInputTypeAttribute, 'checkbox' | 'radio'>;
} & BaseFieldProps;

export const maskTrackingAttributes = { 'data-hj-suppress': true, 'data-matomo-mask': true };

/**
 * This is a barebone inputfield without dependency of any context, and no formik 'magic'.
 * Use FormikInput instead.
 */
export default function TextInput({
    name,
    label,
    id,
    error,
    tabIndex,
    uistate,
    helptext,
    className: outerClassName,
    type,
    ...attr
}: TextInputProps & UiStateProps) {
    const errorId = `${id}error`;
    tabIndex = tabIndex || 0;
    const [HelpBox, HelpBoxIcon] = useHelpBox(helptext, name);
    switch (type) {
        case 'hidden':
            return (
                <input
                    name={name}
                    tabIndex={tabIndex}
                    id={id}
                    type="hidden"
                    {...attr}
                    aria-errormessage={errorId}
                    aria-invalid={error ? true : undefined}
                    {...maskTrackingAttributes}
                />
            );
        case 'textarea': {
            return (
                <div className={joinNames(style.root, outerClassName)}>
                    <div className={style.labelWrapper}>
                        <Label htmlFor={id}>{label}</Label>
                        <HelpBoxIcon />
                    </div>
                    <HelpBox />
                    <div className={joinNames(style.wrapper, uistate == 'value-verified' && style.valueVerified, error ? style.error : undefined)}>
                        {/*@ts-expect-error We asume changehandlers are same */}
                        <textarea
                            name={name}
                            className={style.textArea}
                            tabIndex={tabIndex}
                            id={id}
                            cols={50}
                            rows={5}
                            wrap={'soft'}
                            {...attr}
                            aria-errormessage={errorId}
                            aria-invalid={error ? true : undefined}
                            {...maskTrackingAttributes}
                        />
                        <NotificationIcon prefixForUiTesting={name} check={uistate == 'value-verified'} alert={!!error && { title: error }} />
                    </div>

                    {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
                </div>
            );
        }
        default:
            return (
                <div className={joinNames(style.root, outerClassName)}>
                    <div className={style.labelWrapper}>
                        <Label htmlFor={id}>{label}</Label>
                        <HelpBoxIcon />
                    </div>
                    <HelpBox />
                    <div className={joinNames(style.wrapper, uistate == 'value-verified' && style.valueVerified, error ? style.error : undefined)}>
                        <input
                            name={name}
                            tabIndex={tabIndex}
                            id={id}
                            type={type}
                            {...attr}
                            aria-errormessage={errorId}
                            aria-invalid={error ? true : undefined}
                            {...maskTrackingAttributes}
                        />
                        <NotificationIcon prefixForUiTesting={name} check={uistate == 'value-verified'} alert={!!error && { title: error }} />
                    </div>

                    {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
                </div>
            );
    }
}
